import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import adminMagicResizeService, {
  MagicResizeCategory,
  MagicResizePreset,
  MagicResizeCategoryCreateRequest,
  MagicResizeCategoryUpdateRequest,
  MagicResizePresetCreateRequest,
  MagicResizePresetUpdateRequest,
} from '../../services/api/adminMagicResizeService';
import {
  TaskyBadge,
  TaskyButton,
  TaskyCheckbox,
  TaskyDialog,
  TaskyErrorState,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTextarea,
} from '../tasky';

interface CategoryFormData {
  name: string;
  display_order: number;
  is_active: boolean;
}

interface PresetFormData {
  category_id: number;
  name: string;
  width: number;
  height: number;
  prompt: string;
  display_order: number;
  is_active: boolean;
}

// 小型アクションボタン共通クラス
const smallBtnBase =
  'px-2.5 py-1 rounded-tasky-xs text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed';
const editBtnClass = `${smallBtnBase} border border-warm-border bg-white text-gray-600 hover:bg-warm-hover`;
const deleteBtnClass = `${smallBtnBase} bg-destructive/10 text-destructive hover:bg-destructive/20`;
const activateBtnClass = `${smallBtnBase} bg-success/10 text-success hover:bg-success/20`;

const MagicResizePresetManager: React.FC = () => {
  const [categories, setCategories] = useState<MagicResizeCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // アコーディオンの開閉状態
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<number>>(new Set());

  // カテゴリモーダル
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MagicResizeCategory | null>(null);
  const [categoryFormData, setCategoryFormData] = useState<CategoryFormData>({
    name: '',
    display_order: 0,
    is_active: true,
  });

  // プリセットモーダル
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<MagicResizePreset | null>(null);
  const [presetFormData, setPresetFormData] = useState<PresetFormData>({
    category_id: 0,
    name: '',
    width: 0,
    height: 0,
    prompt: '',
    display_order: 0,
    is_active: true,
  });

  // 削除確認
  const [deleteCategoryConfirmId, setDeleteCategoryConfirmId] = useState<number | null>(null);
  const [deletePresetConfirmId, setDeletePresetConfirmId] = useState<number | null>(null);

  // カテゴリ取得
  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminMagicResizeService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch magic resize categories:', err);
      setError('カテゴリの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // アコーディオンの開閉
  const toggleCategoryExpansion = (categoryId: number) => {
    const newExpanded = new Set(expandedCategoryIds);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategoryIds(newExpanded);
  };

  // カテゴリ作成モーダルを開く
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      display_order: categories.length > 0 ? Math.max(...categories.map(c => c.display_order)) + 1 : 1,
      is_active: true,
    });
    setIsCategoryModalOpen(true);
  };

  // カテゴリ編集モーダルを開く
  const handleEditCategory = (category: MagicResizeCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      display_order: category.display_order,
      is_active: category.is_active,
    });
    setIsCategoryModalOpen(true);
  };

  // カテゴリ保存
  const handleSubmitCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (editingCategory) {
        const updateData: MagicResizeCategoryUpdateRequest = {
          name: categoryFormData.name,
          display_order: categoryFormData.display_order,
          is_active: categoryFormData.is_active,
        };
        await adminMagicResizeService.updateCategory(editingCategory.id, updateData);
      } else {
        const createData: MagicResizeCategoryCreateRequest = {
          name: categoryFormData.name,
          display_order: categoryFormData.display_order,
          is_active: categoryFormData.is_active,
        };
        await adminMagicResizeService.createCategory(createData);
      }
      setIsCategoryModalOpen(false);
      setEditingCategory(null);
      await fetchCategories();
    } catch (err) {
      console.error('カテゴリの保存に失敗しました:', err);
      setError('カテゴリの保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カテゴリ削除
  const handleDeleteCategory = async (categoryId: number) => {
    setIsLoading(true);
    try {
      await adminMagicResizeService.deleteCategory(categoryId);
      setDeleteCategoryConfirmId(null);
      await fetchCategories();
    } catch (err: any) {
      console.error('カテゴリの削除に失敗しました:', err);
      const errorMessage = err.response?.data?.message || 'カテゴリの削除に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // カテゴリ有効/無効切り替え
  const handleToggleCategory = async (categoryId: number) => {
    setIsLoading(true);
    try {
      await adminMagicResizeService.toggleCategory(categoryId);
      await fetchCategories();
    } catch (err) {
      console.error('カテゴリの状態変更に失敗しました:', err);
      setError('カテゴリの状態変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // プリセット作成モーダルを開く
  const handleCreatePreset = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    const presetsInCategory = category?.presets || [];

    setEditingPreset(null);
    setPresetFormData({
      category_id: categoryId,
      name: '',
      width: 0,
      height: 0,
      prompt: '',
      display_order: presetsInCategory.length > 0 ? Math.max(...presetsInCategory.map(p => p.display_order)) + 1 : 1,
      is_active: true,
    });
    setIsPresetModalOpen(true);
  };

  // プリセット編集モーダルを開く
  const handleEditPreset = (preset: MagicResizePreset) => {
    setEditingPreset(preset);
    setPresetFormData({
      category_id: preset.category_id,
      name: preset.name,
      width: preset.width,
      height: preset.height,
      prompt: preset.prompt || '',
      display_order: preset.display_order,
      is_active: preset.is_active,
    });
    setIsPresetModalOpen(true);
  };

  // プリセット保存
  const handleSubmitPreset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (editingPreset) {
        const updateData: MagicResizePresetUpdateRequest = {
          category_id: presetFormData.category_id,
          name: presetFormData.name,
          width: presetFormData.width,
          height: presetFormData.height,
          prompt: presetFormData.prompt.trim() || null,
          display_order: presetFormData.display_order,
          is_active: presetFormData.is_active,
        };
        await adminMagicResizeService.updatePreset(editingPreset.id, updateData);
      } else {
        const createData: MagicResizePresetCreateRequest = {
          category_id: presetFormData.category_id,
          name: presetFormData.name,
          width: presetFormData.width,
          height: presetFormData.height,
          prompt: presetFormData.prompt.trim() || null,
          display_order: presetFormData.display_order,
          is_active: presetFormData.is_active,
        };
        await adminMagicResizeService.createPreset(createData);
      }
      setIsPresetModalOpen(false);
      setEditingPreset(null);
      await fetchCategories();
    } catch (err) {
      console.error('プリセットの保存に失敗しました:', err);
      setError('プリセットの保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // プリセット削除
  const handleDeletePreset = async (presetId: number) => {
    setIsLoading(true);
    try {
      await adminMagicResizeService.deletePreset(presetId);
      setDeletePresetConfirmId(null);
      await fetchCategories();
    } catch (err: any) {
      console.error('プリセットの削除に失敗しました:', err);
      const errorMessage = err.response?.data?.message || 'プリセットの削除に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // プリセット有効/無効切り替え
  const handleTogglePreset = async (presetId: number) => {
    setIsLoading(true);
    try {
      await adminMagicResizeService.togglePreset(presetId);
      await fetchCategories();
    } catch (err) {
      console.error('プリセットの状態変更に失敗しました:', err);
      setError('プリセットの状態変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ソート済みカテゴリ
  const sortedCategories = [...categories].sort((a, b) => {
    if (a.display_order !== b.display_order) {
      return a.display_order - b.display_order;
    }
    return a.name.localeCompare(b.name);
  });

  if (isLoading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center gap-3 py-10">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-500">読み込み中...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="m-0 text-base font-semibold text-dark">マジックリサイズ プリセット管理</h2>
        <TaskyButton
          type="button"
          size="sm"
          onClick={handleCreateCategory}
          disabled={isLoading}
        >
          <Plus />
          新規カテゴリ追加
        </TaskyButton>
      </div>

      {error && (
        <TaskyErrorState
          className="mb-4"
          description={error}
          action={
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="エラーを閉じる"
            >
              <X className="w-4 h-4" />
            </button>
          }
        />
      )}

      <div>
        {sortedCategories.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">
            カテゴリが登録されていません
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {sortedCategories.map((category) => {
              const isExpanded = expandedCategoryIds.has(category.id);
              const sortedPresets = [...(category.presets || [])].sort((a, b) => {
                if (a.display_order !== b.display_order) {
                  return a.display_order - b.display_order;
                }
                return a.name.localeCompare(b.name);
              });

              return (
                <div
                  key={category.id}
                  className="bg-white border border-warm-border rounded-tasky-md overflow-hidden"
                >
                  {/* カテゴリヘッダー */}
                  <div
                    className="flex items-center justify-between p-4 bg-warm-surface hover:bg-warm-hover cursor-pointer transition-colors"
                    onClick={() => toggleCategoryExpansion(category.id)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      <strong className="text-sm font-semibold text-dark">{category.name}</strong>
                      <TaskyBadge variant={category.is_active ? 'success' : 'secondary'}>
                        {category.is_active ? '有効' : '無効'}
                      </TaskyBadge>
                      <TaskyBadge variant="warm">
                        {category.presets_count || 0}件
                      </TaskyBadge>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={editBtnClass}
                        onClick={() => handleEditCategory(category)}
                        disabled={isLoading}
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        className={category.is_active ? deleteBtnClass : activateBtnClass}
                        onClick={() => handleToggleCategory(category.id)}
                        disabled={isLoading}
                      >
                        {category.is_active ? '無効にする' : '有効にする'}
                      </button>
                      {deleteCategoryConfirmId === category.id ? (
                        <>
                          <button
                            type="button"
                            className={deleteBtnClass}
                            onClick={() => handleDeleteCategory(category.id)}
                            disabled={isLoading}
                          >
                            削除実行
                          </button>
                          <button
                            type="button"
                            className={editBtnClass}
                            onClick={() => setDeleteCategoryConfirmId(null)}
                            disabled={isLoading}
                          >
                            キャンセル
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={deleteBtnClass}
                          onClick={() => setDeleteCategoryConfirmId(category.id)}
                          disabled={isLoading || (category.presets_count || 0) > 0}
                          title={
                            (category.presets_count || 0) > 0
                              ? 'このカテゴリは使用中のため削除できません'
                              : '削除'
                          }
                        >
                          削除
                        </button>
                      )}
                    </div>
                  </div>

                  {/* プリセット一覧（展開時） */}
                  {isExpanded && (
                    <div className="p-4 bg-white">
                      {sortedPresets.length === 0 ? (
                        <p className="text-sm text-gray-400 mb-4">
                          プリセットがありません
                        </p>
                      ) : (
                        <div className="mb-4">
                          <TaskyTable>
                            <TaskyTableHead>
                              <TaskyTableRow hoverable={false}>
                                <TaskyTableHeaderCell>プリセット名</TaskyTableHeaderCell>
                                <TaskyTableHeaderCell>サイズ (幅×高さ)</TaskyTableHeaderCell>
                                <TaskyTableHeaderCell>並び順</TaskyTableHeaderCell>
                                <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
                                <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
                              </TaskyTableRow>
                            </TaskyTableHead>
                            <TaskyTableBody>
                              {sortedPresets.map((preset) => (
                                <TaskyTableRow key={preset.id}>
                                  <TaskyTableCell>{preset.name}</TaskyTableCell>
                                  <TaskyTableCell>
                                    {preset.width} × {preset.height}
                                  </TaskyTableCell>
                                  <TaskyTableCell>{preset.display_order}</TaskyTableCell>
                                  <TaskyTableCell>
                                    <TaskyBadge variant={preset.is_active ? 'success' : 'secondary'}>
                                      {preset.is_active ? '有効' : '無効'}
                                    </TaskyBadge>
                                  </TaskyTableCell>
                                  <TaskyTableCell>
                                    <div className="flex gap-1.5 whitespace-nowrap">
                                      <button
                                        type="button"
                                        className={editBtnClass}
                                        onClick={() => handleEditPreset(preset)}
                                        disabled={isLoading}
                                      >
                                        編集
                                      </button>
                                      <button
                                        type="button"
                                        className={preset.is_active ? deleteBtnClass : activateBtnClass}
                                        onClick={() => handleTogglePreset(preset.id)}
                                        disabled={isLoading}
                                      >
                                        {preset.is_active ? '無効にする' : '有効にする'}
                                      </button>
                                      {deletePresetConfirmId === preset.id ? (
                                        <>
                                          <button
                                            type="button"
                                            className={deleteBtnClass}
                                            onClick={() => handleDeletePreset(preset.id)}
                                            disabled={isLoading}
                                          >
                                            削除実行
                                          </button>
                                          <button
                                            type="button"
                                            className={editBtnClass}
                                            onClick={() => setDeletePresetConfirmId(null)}
                                            disabled={isLoading}
                                          >
                                            キャンセル
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          type="button"
                                          className={deleteBtnClass}
                                          onClick={() => setDeletePresetConfirmId(preset.id)}
                                          disabled={isLoading}
                                        >
                                          削除
                                        </button>
                                      )}
                                    </div>
                                  </TaskyTableCell>
                                </TaskyTableRow>
                              ))}
                            </TaskyTableBody>
                          </TaskyTable>
                        </div>
                      )}
                      <TaskyButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCreatePreset(category.id)}
                        disabled={isLoading}
                      >
                        <Plus />
                        サイズを追加
                      </TaskyButton>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* カテゴリモーダル */}
      <TaskyDialog
        open={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        title={editingCategory ? 'カテゴリ編集' : '新規カテゴリ作成'}
        size="md"
      >
        <form onSubmit={handleSubmitCategory}>
          <TaskyFormGroup label="カテゴリ名" required htmlFor="category-name" className="mb-4">
            <TaskyInput
              type="text"
              id="category-name"
              value={categoryFormData.name}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
              required
              disabled={isLoading}
              className="text-sm"
            />
          </TaskyFormGroup>

          <TaskyFormGroup label="並び順" htmlFor="category-display-order" className="mb-4">
            <TaskyInput
              type="number"
              id="category-display-order"
              value={categoryFormData.display_order}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, display_order: parseInt(e.target.value) || 0 })}
              disabled={isLoading}
              min={0}
              className="text-sm"
            />
          </TaskyFormGroup>

          <div className="mb-4">
            <TaskyCheckbox
              label={<span className="font-medium">有効</span>}
              checked={categoryFormData.is_active}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, is_active: e.target.checked })}
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-warm-border">
            <TaskyButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsCategoryModalOpen(false)}
              disabled={isLoading}
            >
              キャンセル
            </TaskyButton>
            <TaskyButton
              type="submit"
              size="sm"
              disabled={isLoading || !categoryFormData.name}
            >
              {isLoading ? '保存中...' : '保存'}
            </TaskyButton>
          </div>
        </form>
      </TaskyDialog>

      {/* プリセットモーダル */}
      <TaskyDialog
        open={isPresetModalOpen}
        onClose={() => setIsPresetModalOpen(false)}
        title={editingPreset ? 'プリセット編集' : '新規プリセット作成'}
        size="md"
      >
        <form onSubmit={handleSubmitPreset}>
          <TaskyFormGroup label="カテゴリ" required htmlFor="preset-category" className="mb-4">
            <TaskySelect
              id="preset-category"
              value={presetFormData.category_id}
              onChange={(e) => setPresetFormData({ ...presetFormData, category_id: parseInt(e.target.value) })}
              required
              disabled={isLoading}
              className="text-sm"
            >
              <option value={0}>カテゴリを選択</option>
              {sortedCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </TaskySelect>
          </TaskyFormGroup>

          <TaskyFormGroup label="プリセット名" required htmlFor="preset-name" className="mb-4">
            <TaskyInput
              type="text"
              id="preset-name"
              value={presetFormData.name}
              onChange={(e) => setPresetFormData({ ...presetFormData, name: e.target.value })}
              required
              disabled={isLoading}
              placeholder="例: Facebook広告"
              className="text-sm"
            />
          </TaskyFormGroup>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <TaskyFormGroup label="幅 (px)" required htmlFor="preset-width">
              <TaskyInput
                type="number"
                id="preset-width"
                value={presetFormData.width}
                onChange={(e) => setPresetFormData({ ...presetFormData, width: parseInt(e.target.value) || 0 })}
                required
                disabled={isLoading}
                min={1}
                className="text-sm"
              />
            </TaskyFormGroup>

            <TaskyFormGroup label="高さ (px)" required htmlFor="preset-height">
              <TaskyInput
                type="number"
                id="preset-height"
                value={presetFormData.height}
                onChange={(e) => setPresetFormData({ ...presetFormData, height: parseInt(e.target.value) || 0 })}
                required
                disabled={isLoading}
                min={1}
                className="text-sm"
              />
            </TaskyFormGroup>
          </div>

          <TaskyFormGroup
            label="リサイズプロンプト（任意）"
            htmlFor="preset-prompt"
            helper="媒体特有のセーフゾーンや構図の指示を記入すると、AIリサイズ時の生成プロンプトに追記され、強めに反映されます。"
            className="mb-4"
          >
            <TaskyTextarea
              id="preset-prompt"
              value={presetFormData.prompt}
              onChange={(e) => setPresetFormData({ ...presetFormData, prompt: e.target.value })}
              disabled={isLoading}
              rows={4}
              placeholder="例: このサイズは上下20%にロゴ・テキストが重なるセーフゾーンです。重要な被写体や文字は中央60%に必ず収め、見切れさせないでください。"
              className="text-sm"
            />
          </TaskyFormGroup>

          <TaskyFormGroup label="並び順" htmlFor="preset-display-order" className="mb-4">
            <TaskyInput
              type="number"
              id="preset-display-order"
              value={presetFormData.display_order}
              onChange={(e) => setPresetFormData({ ...presetFormData, display_order: parseInt(e.target.value) || 0 })}
              disabled={isLoading}
              min={0}
              className="text-sm"
            />
          </TaskyFormGroup>

          <div className="mb-4">
            <TaskyCheckbox
              label={<span className="font-medium">有効</span>}
              checked={presetFormData.is_active}
              onChange={(e) => setPresetFormData({ ...presetFormData, is_active: e.target.checked })}
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-warm-border">
            <TaskyButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsPresetModalOpen(false)}
              disabled={isLoading}
            >
              キャンセル
            </TaskyButton>
            <TaskyButton
              type="submit"
              size="sm"
              disabled={isLoading || !presetFormData.name || presetFormData.category_id === 0 || presetFormData.width === 0 || presetFormData.height === 0}
            >
              {isLoading ? '保存中...' : '保存'}
            </TaskyButton>
          </div>
        </form>
      </TaskyDialog>
    </div>
  );
};

export default MagicResizePresetManager;
